import { UUID_REGEX } from "@bindery/shared/auth/authConstants";
import type { SessionAnswer } from "@/contracts/session";
import { readerIdOf, type SessionState } from "@/convex/session";
import { storageKey } from "@/site";
import {
  isUnconfirmed,
  landedBookmark,
  unconfirmedReaders,
} from "@/storage/unconfirmed-reader";

export const OFFLINE_READER_KEY = storageKey("offlineReader.v1");

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface OfflineIdentity {
  readerId: string;
  lane: SessionAnswer["lane"];
}

export interface OfflineReader {
  /** Feeds every session answer in; the sync triggers call it. */
  note: (state: SessionState) => void;
  /** Who reads while the session is unavailable; null otherwise. */
  identityOf: (state: SessionState) => OfflineIdentity | null;
  /** "That's me": the scanned reader becomes the device's offline reader. */
  confirm: (readerId: string) => void;
  /** Drops the reader from both the page's memory and the device's key. */
  forget: (readerId: string) => void;
  /** Drops whichever reader it holds, as any sign-out must. */
  clear: () => void;
}

const browserStorage = (): Store | null => {
  try {
    return localStorage;
  } catch {
    return null;
  }
};

const browserSearch = (): string =>
  typeof location === "undefined" ? "" : location.search;

function parse(raw: string | null): OfflineIdentity | null {
  if (raw === null) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (
      typeof value === "object" &&
      value !== null &&
      "readerId" in value &&
      typeof value.readerId === "string" &&
      UUID_REGEX.test(value.readerId) &&
      "lane" in value &&
      (value.lane === "reader_session" || value.lane === "user_session")
    ) {
      return { readerId: value.readerId, lane: value.lane };
    }
  } catch {
    // Unreadable: no offline reader.
  }
  return null;
}

/**
 * The reader to read as while the session cannot be reached. Reading needs
 * identification only (P1), and what it queues flushes only once that
 * reader's own session answers.
 *
 * Its own key, not lastReader: lastReader follows reader_session answers
 * only, so after a user_session for another reader it would name the wrong
 * child. Within a page, the reader of the last ready answer wins over the
 * key, so a session dropping mid-reading never switches readers.
 */
export function createOfflineReader({
  storage = browserStorage,
  search = browserSearch,
}: {
  storage?: () => Store | null;
  search?: () => string;
} = {}): OfflineReader {
  let pageIdentity: OfflineIdentity | null = null;
  const pendingScan = (readerId: string) => isUnconfirmed(readerId, storage());
  const remember = (identity: OfflineIdentity) => {
    pageIdentity = identity;
    storage()?.setItem(OFFLINE_READER_KEY, JSON.stringify(identity));
  };

  return {
    note(state) {
      if (state.status === "signed-out") {
        pageIdentity = null;
        return;
      }
      if (state.status !== "ready") return;
      const { lane, readerId } = state.session;
      if (readerId === null) {
        pageIdentity = null;
        storage()?.removeItem(OFFLINE_READER_KEY);
        return;
      }
      // A scanned reader holds the page unconfirmed, so a drop never falls
      // back to the key's earlier reader; the key waits for "That's me".
      if (pendingScan(readerId)) {
        pageIdentity = { readerId, lane };
        return;
      }
      remember({ readerId, lane });
    },
    confirm(readerId) {
      remember({
        readerId,
        lane:
          pageIdentity?.readerId === readerId
            ? pageIdentity.lane
            : "reader_session",
      });
    },
    identityOf(state) {
      if (state.status !== "unavailable") return null;
      if (pageIdentity !== null) {
        return pendingScan(pageIdentity.readerId) ? null : pageIdentity;
      }
      const store = storage();
      if (unconfirmedReaders(store).length > 0) return null;
      if (landedBookmark(search())) return null;
      return parse(store?.getItem(OFFLINE_READER_KEY) ?? null);
    },
    forget(readerId) {
      if (pageIdentity?.readerId === readerId) pageIdentity = null;
      const store = storage();
      const stored = parse(store?.getItem(OFFLINE_READER_KEY) ?? null);
      if (stored?.readerId === readerId) store?.removeItem(OFFLINE_READER_KEY);
    },
    clear() {
      pageIdentity = null;
      storage()?.removeItem(OFFLINE_READER_KEY);
    },
  };
}

export const offlineReader = createOfflineReader();

export const confirmOfflineReader = (readerId: string): void =>
  offlineReader.confirm(readerId);

/** For every sign-out, whichever lane it ends. */
export const clearOfflineReader = (): void => offlineReader.clear();

/** Who this device reads as: the session's reader, else the offline reader. */
export function localIdentityOf(state: SessionState): OfflineIdentity | null {
  const readerId = readerIdOf(state);
  if (state.status === "ready" && readerId !== null) {
    return { readerId, lane: state.session.lane };
  }
  return offlineReader.identityOf(state);
}

/** Whose rows this device reads and writes locally. */
export const localReaderOf = (state: SessionState): string | null =>
  localIdentityOf(state)?.readerId ?? null;
